import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural } from "@lingui/react/macro"
import { Box, Loader, Progress } from "@mantine/core"
import { formatClock } from "../../../lib/format-date"
import type { TProcessingStage } from "../../../lib/pipeline-progress"
import { useProduct } from "../../../lib/tenant"
import { displayPercent, stepsForMode, type TFinalizeMode } from "../progress"
import type { IProps } from "./interfaces"
import styles from "./styles.module.css"

interface IStageCopy {
  label: string
  description: string
}

interface IAnimatedCheckmarkProps {
  pathClassName?: string
  size?: number
}

function AnimatedCheckmark({
  pathClassName = styles.lexCheckPath,
  size = 16,
}: IAnimatedCheckmarkProps): React.JSX.Element {
  return (
    <svg viewBox="0 0 20 20" fill="none" width={size} height={size} aria-hidden="true" className={styles.check}>
      <path
        d="M5 10.5l3.5 3.5L15 7"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength="1"
        className={pathClassName}
      />
    </svg>
  )
}

function lexCopy(i18n: I18n): Record<TProcessingStage, IStageCopy> {
  return {
    uploading: {
      label: i18n._(msg`Saving the audio`),
      description: i18n._(msg`The recording is being stored`),
    },
    transcribing: {
      label: i18n._(msg`Writing the transcript`),
      description: i18n._(msg`Turning speech into text`),
    },
    identifying_speakers: {
      label: i18n._(msg`Telling voices apart`),
      description: i18n._(msg`Matching each voice`),
    },
    analyzing: {
      label: i18n._(msg`Writing the summary`),
      description: i18n._(msg`Decisions, action items and a short summary`),
    },
    complete: {
      label: i18n._(msg`Done`),
      description: i18n._(msg`Your meeting is ready`),
    },
  }
}

function voxCopy(): Record<TProcessingStage, IStageCopy> {
  return {
    uploading: { label: "Archiving the session", description: "Sealing the recording in the vault" },
    transcribing: { label: "Deciphering the chronicles", description: "Translating the spoken word to script" },
    identifying_speakers: { label: "Naming the heroes", description: "Attributing each deed to its champion" },
    analyzing: { label: "Weaving the tale", description: "Spinning the threads into a saga" },
    complete: { label: "The legend is written", description: "Your chronicle awaits" },
  }
}

/** Full keeps the summary wording. Lite's last step is only a title. */
function copyFor(product: string, mode: TFinalizeMode, i18n: I18n): Record<TProcessingStage, IStageCopy> {
  const base = product === "voxcanon" ? voxCopy() : lexCopy(i18n)
  if (mode !== "lite") return base
  return {
    ...base,
    analyzing: {
      label: i18n._(msg`Choosing a title`),
      description: i18n._(msg`Based on what was said`),
    },
  }
}

function LiveDetail({
  stage,
  voiceCount,
  segmentCount,
}: {
  stage: TProcessingStage
  voiceCount: number
  segmentCount: number
}): React.JSX.Element | null {
  if (stage === "identifying_speakers" && voiceCount > 0) {
    return <Plural value={voiceCount} one="# voice so far" other="# voices so far" />
  }
  if (stage === "transcribing" && segmentCount > 0) {
    return <Plural value={segmentCount} one="# line so far" other="# lines so far" />
  }
  return null
}

export function FinalizationProgressView({
  mode,
  stepIndex,
  percent,
  isComplete,
  elapsedSeconds = 0,
  voiceCount = 0,
  segmentCount = 0,
}: IProps): React.JSX.Element {
  const product = useProduct()
  const { i18n } = useLingui()
  const steps = stepsForMode(mode)
  const total = steps.length
  const safeIndex = Math.max(0, Math.min(stepIndex, total))
  const activeStage = isComplete ? null : (steps[safeIndex] ?? null)
  const pct = displayPercent(percent, isComplete)
  const copy = copyFor(product, mode, i18n)
  const detail =
    activeStage === null ? null : <LiveDetail stage={activeStage} voiceCount={voiceCount} segmentCount={segmentCount} />

  if (product === "lexicanon") {
    const stepNumber = Math.min(safeIndex + 1, total)
    return (
      <Box
        className={`${styles.lexPanel} lex-surface-card`}
        mb="md"
        data-mode={mode}
        data-testid="finalization-progress"
      >
        <div className={styles.lexHeader}>
          <span className={styles.lexHeaderTitle}>
            {isComplete
              ? i18n._(msg`Done`)
              : mode === "lite"
                ? i18n._(msg`Finishing your recording`)
                : i18n._(msg`Finishing your meeting`)}
          </span>
          <span className={styles.lexHeaderMeta}>
            {isComplete ? (
              i18n._(msg`Your meeting is ready`)
            ) : (
              <>
                {i18n._(msg`Step ${stepNumber} of ${total}`)} · {pct}%
                {elapsedSeconds >= 3 ? ` · ${formatClock(elapsedSeconds)}` : ""}
              </>
            )}
          </span>
        </div>

        <Progress
          className={styles.lexProgress}
          value={isComplete ? 100 : percent}
          color={isComplete ? "success" : "brand"}
          radius="xl"
          size="sm"
          striped={!isComplete}
          animated={!isComplete}
          transitionDuration={600}
        />

        <ol className={styles.lexSteps}>
          {steps.map((step, i) => {
            const isDone = isComplete || safeIndex > i
            const isActive = !isComplete && i === safeIndex
            const stateClass = isDone ? styles.lexStepDone : isActive ? styles.lexStepActive : styles.lexStepPending
            return (
              <li key={step} className={`${styles.lexStep} ${stateClass}`}>
                <span className={styles.lexNode}>
                  {isDone ? <AnimatedCheckmark size={15} /> : isActive ? <Loader size={14} color="brand" /> : i + 1}
                </span>
                <div className={styles.lexStepBody}>
                  <span className={styles.lexStepLabel}>{copy[step].label}</span>
                  <span className={styles.lexStepDesc}>{copy[step].description}</span>
                  {isActive && detail ? <span className={styles.lexStepCurrent}>{detail}</span> : null}
                  {isActive ? <span className={styles.lexWorkingBar} aria-hidden="true" /> : null}
                </div>
              </li>
            )
          })}
        </ol>
      </Box>
    )
  }

  return (
    <Box className={styles.voxStepper} mb="md" data-mode={mode} data-testid="finalization-progress">
      <div className={styles.voxTitle}>⚔ Processing Session ⚔</div>
      {steps.map((step, i) => {
        const isDone = isComplete || safeIndex > i
        const isActive = !isComplete && i === safeIndex
        const icon = isDone ? <AnimatedCheckmark pathClassName={styles.voxCheckPath} size={14} /> : isActive ? "✦" : "·"
        return (
          <div
            key={step}
            className={`${styles.voxStep} ${isActive ? styles.voxStepActive : ""} ${isDone ? styles.voxStepComplete : ""}`}
          >
            <span className={styles.voxIcon}>{icon}</span>
            <span className={styles.voxBody}>
              <span className={styles.voxLabel}>{copy[step].label}</span>
              <span className={styles.voxDesc}>{copy[step].description}</span>
              {isActive && detail ? <span className={styles.voxDetail}>{detail}</span> : null}
            </span>
          </div>
        )
      })}
      {isComplete && <div className={styles.voxComplete}>{copy.complete.label}</div>}
    </Box>
  )
}
